import { routes } from '@/domains/core/routing/routes/routes.default';
import { createURL } from '@/domains/core/routing/URLParser';
import { IS_B2B } from '@/domains/core/settings/constants';
import { isUkPlatform } from '@/domains/productDiscovery/ProductPage/utils/platform/isPlatform';
import { articlePathToSlugId } from '@/domains/productDiscovery/ProductPage/utils/productUrl/articlePathToSlugId';
import { processRetailPriceTimeLeftLabel } from '@/productDiscovery/CommercialAnimation/utils/commercialOperations.processor';

import {
  AlgoliaHit,
  RawPricesByType,
  SponsoredData,
} from '../../interfaces/algoliaResponse';
import type { Product } from '../../interfaces/product';

export const processHitsProducts = (
  hits: (AlgoliaHit & SponsoredData)[],
): Product[] =>
  hits.map((hit) => ({
    productId: hit.objectID,
    title: hit.title,
    articleId: hit.article_id,
    url: generateUrl(hit),
    brandImageFullpath: hit.brand_image_fullpath,
    brandName: hit.brand_name,
    brandId: hit.brand_id,
    prices: generatePrices(hit),
    modelId: hit.model_id,
    imageFullpath: hit.image_fullpath,
    isCompetitive: hit.is_competitive,
    commercialOperationIds: hit.commercial_operation_ids,
    ...(hit.commercial_animation && {
      commercialAnimation: {
        tags: hit.commercial_animation.tags || [],
        promotionEndDate: hit.commercial_animation.promotion_end_at || null,
        retailPriceTimeLeftLabel: hit.commercial_animation.promotion_end_at
          ? processRetailPriceTimeLeftLabel(
              hit.commercial_animation.promotion_end_at,
            )
          : null,
      },
    }),
    modelsCount: hit.models_count,
    categoriesLastId: hit['categories.last_id'],
    categoriesLast: hit['categories.last'],
    sellerId: hit.seller_id,
    sellerName: hit.seller_name,
    isMmf: hit.is_mmf,
    isB2b: hit.is_b2b || false,
    isSponsored: hit.isSponsored || false,
    position: hit.position || null,
    adPayer: hit.adPayer,
    cpc: hit.cpc,
    adTrackingData: hit.adTrackingData || '',
  }));

function generateUrl(hit: AlgoliaHit) {
  let url = createURL(
    routes.product,
    articlePathToSlugId(`${hit.url}-${hit.article_id}`),
  );

  if (hit.models_count > 1) {
    url = `${url}?model_id=${hit.model_id}`;
  }

  return url;
}

export function getTaxKey() {
  return IS_B2B && !isUkPlatform() ? 'without_vat' : 'with_vat';
}

function generatePrices(hit: AlgoliaHit): Product['prices'] {
  const taxKey = getTaxKey();
  const main =
    hit.prices.per_item.actual_price[taxKey].as_float * (hit.min_quantity || 1);

  const retail = getPriceByKey(hit, hit.prices.main_price)?.retail_price?.[
    taxKey
  ].as_float;

  return {
    main,
    retail: retail || null,
  };
}

function getPriceByKey(
  hit: AlgoliaHit,
  key: 'per_item' | null,
): RawPricesByType | undefined {
  switch (key) {
    case 'per_item':
      return hit.prices.per_item;

    default:
      return undefined;
  }
}
